import { Stack, styled } from "@mui/material";
import { EyeIcon } from "@phosphor-icons/react/dist/csr/Eye";
import { Dispatch, useEffect, useState } from "react";
import useAuth, { DataGridColumns, DataGridPreferences, DataGrids } from "../../context/UserContext";
import { NewSearchTextBox } from "../../features/data-under-management/components/NewSearchTextBox";
import { CheckboxFilters } from "../../features/keys/components/CheckboxFilters";
import { useDebounce } from "../../lib/Hooks";
import { ClearThisFilter, FilterPanel, FilterRow, Item, SelectableItems } from "../Filters/Common";
import { DateRangeFilters } from "../Filters/DateRangeFilters";
import { ExportButton } from "../Filters/ExportButton";
import { ProjectsFilter } from "../Filters/ProjectsFilter";
import { PurposeFilters } from "../Filters/PurposeFilters";
import { ImportFilter, SourceNode, SourceLeaf } from "../common/FilterPublishedArtifacts";
import { IDownloadFilters } from "./downloadsDatagridUtils";

const StyledEye = styled(EyeIcon)(({ theme }) => ({
  color: theme.palette.text50.main,
}));

const CHECKBOX_OPTIONS = [
  { value: DataGridColumns.ReportDateTime, label: "Report date" },
  { value: DataGridColumns.Created, label: "Created" },
  { value: DataGridColumns.Started, label: "Started" },
  { value: DataGridColumns.Finished, label: "Finished" },
  { value: DataGridColumns.Project, label: "Project name" },
  { value: DataGridColumns.Name, label: "Name" },
  { value: DataGridColumns.DownloadDuration, label: "Download duration" },
  { value: DataGridColumns.QueuedDuration, label: "Queued duration" },
  { value: DataGridColumns.Purpose, label: "Purpose" },
  { value: DataGridColumns.Description, label: "Description" },
  { value: DataGridColumns.Status, label: "Status" },
  { value: DataGridColumns.Actions, label: "Actions" },
];

const COLUMNS = [
  DataGridColumns.ReportDateTime,
  DataGridColumns.Created,
  DataGridColumns.Started,
  DataGridColumns.Finished,
  DataGridColumns.Project,
  DataGridColumns.Name,
  DataGridColumns.DownloadDuration,
  DataGridColumns.QueuedDuration,
  DataGridColumns.Purpose,
  DataGridColumns.Description,
  DataGridColumns.Status,
  DataGridColumns.Actions,
];

const StatusFilters = ({
  selected,
  onClick,
  onClearThisFilter,
}: {
  selected: string;
  onClick: (purpose: string) => void;
  onClearThisFilter: () => void;
}) => {
  return (
    <Stack sx={{ gap: "12px" }}>
      <FilterRow value="completed" onClick={onClick} text="Completed" isSelected={selected === "completed"} />
      <FilterRow
        value="dirtyEqualsNewDirty"
        onClick={onClick}
        text="Dirty Rows (new = current)"
        isSelected={selected === "dirtyEqualsNewDirty"}
      />
      <FilterRow
        value="newDirtyGreaterThanDirty"
        onClick={onClick}
        text="New Dirty Rows (new > current)"
        isSelected={selected === "newDirtyGreaterThanDirty"}
      />
      <FilterRow value="failed" onClick={onClick} text="Failed" isSelected={selected === "failed"} />
      <FilterRow value="skipped" onClick={onClick} text="Skipped" isSelected={selected === "skipped"} />
      <FilterRow value="unfinished" onClick={onClick} text="Unfinished" isSelected={selected === "unfinished"} />
      {selected ? <ClearThisFilter onClick={onClearThisFilter} /> : null}
    </Stack>
  );
};

const FilterItems = ({
  selectedItem,
  setSelectedItem,
  filter,
}: {
  selectedItem: SelectableItems;
  setSelectedItem: Dispatch<React.SetStateAction<SelectableItems>>;
  filter: IDownloadFilters;
}) => {
  return (
    <>
      <Item
        text="Report date"
        isSelected={selectedItem === "reportDate"}
        onClick={() => setSelectedItem("reportDate")}
        hasFiltersApplied={!!(filter.reportRange.start && filter.reportRange.end)}
      />
      <Item
        text="Started"
        isSelected={selectedItem === "startedDate"}
        onClick={() => setSelectedItem("startedDate")}
        hasFiltersApplied={!!(filter.downloadedRange.start && filter.downloadedRange.end)}
      />
      <Item
        text="Name"
        isSelected={selectedItem === "name"}
        onClick={() => setSelectedItem("name")}
        hasFiltersApplied={!!filter.projects?.length || !!filter.importNames.length}
      />
      <Item
        text="Purpose"
        isSelected={selectedItem === "purpose"}
        onClick={() => setSelectedItem("purpose")}
        hasFiltersApplied={!!filter.purpose}
      />
      <Item
        text="Status"
        isSelected={selectedItem === "status"}
        onClick={() => setSelectedItem("status")}
        hasFiltersApplied={!!filter.status}
      />
    </>
  );
};

type RightPanelProps = {
  filter: IDownloadFilters;
  setFilter: Dispatch<React.SetStateAction<IDownloadFilters>>;
  selectedItem: SelectableItems;
  selectedNodesInFilter: SourceNode[];
  nonSelectedNodesInFilter: SourceNode[];
};

const RightPanel = ({
  filter,
  setFilter,
  selectedItem,
  selectedNodesInFilter,
  nonSelectedNodesInFilter,
}: RightPanelProps) => {
  const handleSelectCallback = (nodes: SourceLeaf[], takeOut?: boolean) => {
    setFilter((prevFilter) => {
      const newFilterValue = {
        bundleNames: new Set(prevFilter.bundleNames),
        importNames: new Set(prevFilter.importNames),
        projects: new Set(prevFilter.projects),
      };

      nodes.forEach((node) => {
        const nodeFilterValue = node.filterValue as ImportFilter;
        if (!nodeFilterValue) {
          return;
        }
        if (takeOut) {
          if (nodeFilterValue.bundleName) {
            newFilterValue.bundleNames.delete(nodeFilterValue.bundleName);
          }
          if (nodeFilterValue.importName) {
            newFilterValue.importNames.delete(nodeFilterValue.importName);
          }
          if (nodeFilterValue.project) {
            newFilterValue.projects.delete(nodeFilterValue.project);
          }
        } else {
          if (nodeFilterValue.bundleName) {
            newFilterValue.bundleNames.add(nodeFilterValue.bundleName);
          }
          if (nodeFilterValue.importName) {
            newFilterValue.importNames.add(nodeFilterValue.importName);
          }
          if (nodeFilterValue.project) {
            newFilterValue.projects.add(nodeFilterValue.project);
          }
        }
      });

      return {
        ...prevFilter,
        bundleNames: Array.from(newFilterValue.bundleNames),
        importNames: Array.from(newFilterValue.importNames),
        projects: Array.from(newFilterValue.projects),
      };
    });
  };

  if (selectedItem === "reportDate") {
    return (
      <DateRangeFilters
        onSelectDate={({ start, end }) => setFilter({ ...filter, reportRange: { start, end } })}
        start={filter.reportRange.start || null}
        end={filter.reportRange.end || null}
        onClearThisFilter={() => setFilter({ ...filter, reportRange: { start: null, end: null } })}
      />
    );
  }

  if (selectedItem === "startedDate") {
    return (
      <DateRangeFilters
        onSelectDate={({ start, end }) => setFilter({ ...filter, downloadedRange: { start, end } })}
        start={filter.downloadedRange.start || null}
        end={filter.downloadedRange.end || null}
        onClearThisFilter={() => setFilter({ ...filter, downloadedRange: { start: null, end: null } })}
      />
    );
  }

  if (selectedItem === "name") {
    return (
      <ProjectsFilter
        onClearThisFilter={() => setFilter({ ...filter, projects: [], importNames: [], bundleNames: [] })}
        selectedNodesInFilter={selectedNodesInFilter}
        nonSelectedNodesInFilter={nonSelectedNodesInFilter}
        onSelectCallback={handleSelectCallback}
      />
    );
  }

  if (selectedItem === "purpose") {
    return (
      <PurposeFilters
        selected={filter.purpose}
        onClick={(purpose) => setFilter({ ...filter, purpose: filter.purpose === purpose ? "" : purpose })}
        onClearThisFilter={() => setFilter({ ...filter, purpose: "" })}
      />
    );
  }

  if (selectedItem === "status") {
    return (
      <StatusFilters
        selected={filter.status}
        onClick={(status) => setFilter({ ...filter, status: filter.status === status ? "" : status })}
        onClearThisFilter={() => setFilter({ ...filter, status: "" })}
      />
    );
  }

  return null;
};

const handleClearAllFilters = (setFilter: Dispatch<React.SetStateAction<IDownloadFilters>>) => {
  setFilter({
    projects: [],
    importNames: [],
    bundleNames: [],
    purpose: "",
    search: "",
    status: "",
    reportRange: {
      start: null,
      end: null,
    },
    downloadedRange: {
      start: null,
      end: null,
    },
    sortBy: undefined,
    sortDirection: undefined,
  });
};

type DownloadFiltersProps = {
  filter: IDownloadFilters;
  setFilter: Dispatch<React.SetStateAction<IDownloadFilters>>;
  queryVariables: DownloadFilters | UploadsFilters;
  selectedNodesInFilter: SourceNode[];
  nonSelectedNodesInFilter: SourceNode[];
  loading: boolean;
  excludeFields?: string[];
  datagridPreferencesElement: DataGridPreferences;
  preferenceElementToUpdate: DataGrids;
  initialVisibilityModel: { [key in DataGridColumns]?: boolean };
  hideTextSearch?: boolean;
};

const isFilteringDownloads = (filter: IDownloadFilters) =>
  Boolean(filter.bundleNames.length) ||
  Boolean(filter.importNames.length) ||
  Boolean(filter.projects.length) ||
  Boolean(filter.purpose) ||
  Boolean(filter.search) ||
  Boolean(filter.status) ||
  Boolean(filter.reportRange.start) ||
  Boolean(filter.reportRange.end) ||
  Boolean(filter.downloadedRange.start) ||
  Boolean(filter.downloadedRange.end);

const DownloadFilters = ({
  filter,
  setFilter,
  queryVariables,
  selectedNodesInFilter,
  nonSelectedNodesInFilter,
  loading,
  excludeFields,
  datagridPreferencesElement,
  preferenceElementToUpdate,
  initialVisibilityModel,
  hideTextSearch,
}: DownloadFiltersProps) => {
  const { updateDataGridPreferences } = useAuth();

  const [searchValue, setSearchValue] = useState(filter.search || "");
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [selectedItem, setSelectedItem] = useState<SelectableItems>("reportDate");

  useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      search: debouncedSearchValue,
    }));
  }, [debouncedSearchValue, setFilter]);

  const getSelectedOptions = () => {
    const columnVisibilityModel = datagridPreferencesElement?.columnVisibilityModel || initialVisibilityModel;
    return COLUMNS.filter((col) => columnVisibilityModel?.[col]);
  };

  const columnsWidth = datagridPreferencesElement?.columnsWidth;
  const showClearAllFilters = isFilteringDownloads(filter);

  return (
    <Stack direction="row" sx={{ gap: "12px", alignItems: "center", height: "48px" }}>
      {hideTextSearch ? null : (
        <Stack sx={{ width: "300px" }}>
          <NewSearchTextBox value={searchValue} onChange={setSearchValue} placeholder="Search" />
        </Stack>
      )}
      <FilterPanel
        loading={loading}
        onClearAllFilters={() => handleClearAllFilters(setFilter)}
        showClearAllFilters={showClearAllFilters}
        filterCount={showClearAllFilters ? 1 : 0}
        rightPanel={
          <RightPanel
            filter={filter}
            setFilter={setFilter}
            selectedItem={selectedItem}
            selectedNodesInFilter={selectedNodesInFilter}
            nonSelectedNodesInFilter={nonSelectedNodesInFilter}
          />
        }
        biggerWidth={selectedItem === "name"}
        filterItems={<FilterItems selectedItem={selectedItem} setSelectedItem={setSelectedItem} filter={filter} />}
      />
      <CheckboxFilters
        Icon={<StyledEye size={24} />}
        options={CHECKBOX_OPTIONS.filter((option) => !excludeFields?.includes(option.value))}
        onClickOption={(value) => {
          const visibilityModel = datagridPreferencesElement?.columnVisibilityModel || initialVisibilityModel;
          updateDataGridPreferences(preferenceElementToUpdate, {
            columnsOrder: datagridPreferencesElement?.columnsOrder,
            columnsWidth: columnsWidth,
            columnVisibilityModel: {
              ...visibilityModel,
              [value]: !visibilityModel[value],
            },
          });
        }}
        selectedOptions={getSelectedOptions()}
      />
      <ExportButton queryVariables={queryVariables} query="downloads" />
    </Stack>
  );
};

export { CHECKBOX_OPTIONS as DOWNLOAD_CHECKBOX_OPTIONS, DownloadFilters, isFilteringDownloads };
